/** Specimen copied from the Nov 2026 Hop Studios soul audit. ASCII only. */

export type Area = 'Workspace' | 'Rowan' | 'Skills/SOPs' | 'Stale'
export type Priority = 'P0' | 'P1' | 'P2'

export type AuditEdit = {
  area: Area
  p: Priority
  edit: string
  file: string
  evidence: string
  draft: string
}

export const AREAS: Array<'All' | Area> = ['All', 'Workspace', 'Rowan', 'Skills/SOPs', 'Stale']

export const CALLOUT = {
  lead: 'Highest leverage',
  body: '1. Add the hours-only estimate rule to limits.md. 2. Split the secrets limit so agents can help with 1Password share links. 3. Rename flownotes_* to maater_* in multi-agent-coordination.md, and fix Rowan\'s stale memory line.',
}

export const EDITS: AuditEdit[] = [
  { area: 'Workspace', p: 'P0', edit: 'Hours-only estimates rule', file: '.soul/limits.md', evidence: 'Chris rule Oct 27; only in create-hop-estimate skill', draft: 'Never put hourly rates or dollar totals in estimates or client drafts. Hours only. If a client asks for a rate, confirm with Chris first.' },
  { area: 'Workspace', p: 'P0', edit: 'Split secrets rule: no plaintext vs approved 1Password path', file: '.soul/limits.md', evidence: 'Limits ban handling passwords; 1password.md SOP + Secure Notes migration exist; Imagenex thread showed confusion', draft: 'Don\'t display secrets in chat. Do help with the approved path: 1Password time-limited share links (incl. Secure Notes) from the Clients vault, never vault access. See hop-tools/1password.md.' },
  { area: 'Workspace', p: 'P0', edit: 'Rename flownotes_* to maater_*', file: '.soul/multi-agent-coordination.md', evidence: 'flownotes_* now flagged deprecated aliases by the MCP', draft: 'Use maater_search, maater_get_recent, maater_get_todo_context, maater_get_document_by_path, maater_update_document, maater_show_changes. flownotes_* are deprecated aliases.' },
  { area: 'Workspace', p: 'P1', edit: 'Tools: add Slack, 1Password, Zoom, Google Drive, uptime, Grok Bot', file: '.soul/organizational-context.md', evidence: 'Communication says Email + Basecamp only; Slack is primary internal chat', draft: 'Internal chat: Slack (#hop-developers, #hop-review, #hop-suggestions). Client comms: Basecamp + email. Meetings: Zoom. Passwords: 1Password. AI: Maater, Cursor, Grok Bot (Hop Bot).' },
  { area: 'Workspace', p: 'P1', edit: 'Complete client list', file: '.soul/identity.md + organizational-context.md', evidence: 'Lists GG, exida, VCR only; clients/ also has NCHFP, Lucis Trust, Imagenex, others', draft: 'Point to hop-studios/clients/ as the canonical list; name top accounts only.' },
  { area: 'Workspace', p: 'P1', edit: 'Workspace routing for Maater work', file: '.soul/multi-agent-coordination.md', evidence: 'Maater bets belong in Maater workspace', draft: 'Maater product bets, specs and issues go in the Maater workspace, not hop-studios. Run the documentation-management cluster check before creating a bet.' },
  { area: 'Workspace', p: 'P2', edit: 'Add weekly rituals', file: '.soul/flows.md', evidence: 'hop-review Thursday count, uptime-outage-watch, Deploy HQ not listed', draft: 'Thursday: #hop-review pins and completed reviews counted for staff meeting. Triggered: uptime-outage-watch.' },
  { area: 'Workspace', p: 'P2', edit: 'Length targets per surface', file: '.soul/communication.md', evidence: '5-15 line targets fit Maater chat, too long for Slack/Grok Bot replies', draft: 'Chat/Slack replies: 1-5 lines. Maater docs and recommendations: existing targets apply.' },
  { area: 'Rowan', p: 'P0', edit: 'Replace stale founder note', file: 'users/rowan/.soul/memory.md', evidence: 'Says sole developer as of May 2026', draft: 'Rowan is Maater product owner and lead developer and a Hop Studios senior dev; switch workspace by topic.' },
  { area: 'Rowan', p: 'P1', edit: 'Draft-don\'t-send default', file: 'users/rowan/.soul/preferences.md', evidence: 'Grok Bot practice: drafts Slack/email for approval', draft: 'Draft Slack and email messages for approval; never send, post or update tickets without explicit go-ahead.' },
  { area: 'Rowan', p: 'P1', edit: 'Tool stack update', file: 'users/rowan/.soul/identity.md', evidence: 'Says Maater, Cursor, Claude; now Grok Bot + Slack, Gmail, Calendar, GitLab, maater-cloud MCP', draft: 'Agents: Maater Ops (Cursor/webapp) and Grok Bot (Hop Bot) for Slack, Gmail, Calendar, GitLab. Shell: ghost-complete replacing kiro-cli autocomplete.' },
  { area: 'Rowan', p: 'P2', edit: 'Dual-hat switching rule', file: 'users/rowan/.soul/identity.md', evidence: 'Hop vs Maater context mixing', draft: 'Client or agency topic: hop-studios. Product, bets, roadmap: Maater workspace. Ask if unclear.' },
  { area: 'Skills/SOPs', p: 'P1', edit: 'Link soul limits to SOPs', file: 'create-hop-estimate, 1password.md', evidence: 'Rules live in skill/SOP but not soul', draft: 'Skill keeps the procedure; soul keeps the one-line rule and a path.' },
  { area: 'Skills/SOPs', p: 'P2', edit: 'Close 1Password migration doc loop', file: 'internal/todos/10338761502-.../plan-of-action.md', evidence: 'Secure Notes migration plan active since Sep 24', draft: 'When done, update hop-tools/1password.md and mark the Sep 22 proposal superseded.' },
  { area: 'Stale', p: 'P1', edit: 'Org table check', file: '.soul/organizational-context.md', evidence: 'Team table pre-dates recent roles; Ting/Gil, STAR not reflected', draft: 'Confirm roles with Tammy; add an updated date line.' },
  { area: 'Stale', p: 'P2', edit: 'Session-end habits', file: 'users/rowan/.soul/preferences.md', evidence: 'STATUS-BOARD, npm run status, DB backup are old local-dev habits', draft: 'Confirm which still apply on cloud-first Maater; drop the rest.' },
]

export const PROS = [
  'Strong client-facing bans: em dashes, chevrons, internal links, vendor names',
  'Clear remote-access and production limits',
  'Approval-gated soul updates',
  'Basecamp cache-freshness and HTML rules',
]

export const CONS = [
  'Business rules (rates) live only in skills',
  'Tool names and stack lag the product',
  'User soul untouched since Sep 1',
  'Assumes one agent surface',
]

export const FILES = [
  { label: '.soul/identity.md', value: 'updated about Jul 2026' },
  { label: '.soul/limits.md', value: 'updated Oct 29 2026' },
  { label: '.soul/values.md, communication.md, organizational-context.md, multi-agent-coordination.md, flows.md', value: 'loaded; dates not rechecked' },
  { label: 'users/rowan/.soul/identity.md', value: 'loaded' },
  { label: 'users/rowan/.soul/preferences.md', value: 'updated Sep 1 2026' },
  { label: 'users/rowan/.soul/memory.md', value: 'updated Sep 1 2026' },
]

export const CHECKLIST = EDITS.filter((edit) => edit.p !== 'P2')

export function toneFor(priority: Priority): 'danger' | 'warning' | undefined {
  if (priority === 'P0') return 'danger'
  if (priority === 'P1') return 'warning'
  return undefined
}

function q(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

export function calloutFence(): string {
  return ['```yaml callout', `lead: ${q(CALLOUT.lead)}`, `body: ${q(CALLOUT.body)}`, '```'].join('\n')
}

export function tableFence(): string {
  const rows = EDITS.map((edit) => {
    const tone = toneFor(edit.p)
    const toneLine = tone ? `\n    tone: ${tone}` : ''
    return [
      `  - area: ${edit.area}`,
      `    pri: ${edit.p}${toneLine}`,
      `    edit: ${q(edit.edit)}`,
      `    file: ${q(edit.file)}`,
      `    evidence: ${q(edit.evidence)}`,
      `    draft: ${q(edit.draft)}`,
    ].join('\n')
  })
  return [
    '```yaml table',
    'filter: area',
    'columns:',
    '  - key: edit',
    '    label: Edit',
    '  - key: pri',
    '    label: Pri',
    '  - key: file',
    '    label: File',
    '  - key: evidence',
    '    label: Evidence',
    '  - key: draft',
    '    label: Draft wording',
    'rows:',
    rows.join('\n'),
    '```',
  ].join('\n')
}

export function prosConsFence(): string {
  const lines = [
    '```yaml pros-cons',
    'pros:',
    ...PROS.map((item) => `  - ${q(item)}`),
    'cons:',
    ...CONS.map((item) => `  - ${q(item)}`),
    '```',
  ]
  return lines.join('\n')
}

export function checklistFence(done: boolean[]): string {
  const items = CHECKLIST.map((edit, index) => {
    return [
      `  - text: ${q(`${edit.edit} (${edit.file})`)}`,
      `    done: ${done[index] ? 'true' : 'false'}`,
      `    meta: ${q(`${edit.p} - awaiting approval, as of Nov 2`)}`,
    ].join('\n')
  })
  return ['```yaml checklist', 'items:', items.join('\n'), '```'].join('\n')
}

export function propertiesFence(): string {
  const items = FILES.map((file) => `  - label: ${q(file.label)}\n    value: ${q(file.value)}`)
  return ['```yaml properties', 'items:', items.join('\n'), '```'].join('\n')
}

export function documentSource(reviewImported: boolean, done: boolean[]): string {
  const kits = reviewImported ? '  - core\n  - review' : '  - core'
  return [
    '---',
    'title: Hop Studios soul audit - Nov 2026',
    'type: note',
    'kits:',
    kits,
    '---',
    '',
    '# Hop Studios soul audit - Nov 2026',
    '',
    'Where the Hop Studios and Rowan soul files drift from how work actually happens, with draft wording to approve. Nothing has been written to soul yet.',
    '',
    '## Top 3 edits',
    '',
    calloutFence(),
    '',
    '## Suggested edits',
    '',
    tableFence(),
    '',
    '## What the soul already does well',
    '',
    prosConsFence(),
    '',
    '## Two agents, one soul',
    '',
    'Maater Ops (Cursor and webapp) reads this soul natively; Grok Bot reads it through maater_get_soul and also works in Slack, Gmail and Calendar. Rules should be surface-neutral, with per-surface length and send behavior stated once in communication.md.',
    '',
    '## Proposed patches',
    '',
    checklistFence(done),
    '',
    '## Files reviewed',
    '',
    propertiesFence(),
    '',
    'Core kit renders the callout, pros and cons, and the file list. The review kit renders the table and the checklist. The area filter changes the view only. A check writes done into the checklist fence. Open Source to see the kits list and the fences.',
    '',
  ].join('\n')
}
